import { useState } from "react";
import { useQuery, useQueryClient } from "@tanstack/react-query";
import { Bookmark } from "lucide-react";
import { toast } from "sonner";
import type { ThreadSummary } from "@shared/api";
import type { MailCategory, MailCollection, SavedMailView } from "@shared/library";
import { Button } from "@web/ui/Button";
import { Modal } from "@web/ui/Modal";
import { runThreadAction } from "@web/inbox/actions/threadActionOps";
import { registerUndo } from "@web/lib/undoRegistry";
import { useCommand } from "@web/shell/commands";
import type { CommandId } from "@web/shell/keymap";
import { CreateCategory, useCategories, categoryError } from "./Categories";
import { libraryApi, libraryKey } from "./api";

const field = "w-full rounded-lg border border-line bg-bg p-2 text-ink";
function SaveForm({
  threadKey,
  initial,
  suggested,
  thread,
  onClose,
  categories,
}: {
  threadKey: string;
  initial: SavedMailView | null;
  thread: ThreadSummary | null;
  suggested?: MailCollection | undefined;
  onClose: () => void;
  categories: MailCategory[];
}) {
  const client = useQueryClient();
  const initialChoice =
    suggested && categories.some((c) => c.id === suggested)
      ? suggested
      : (categories.find((c) => c.id === "reading")?.id ?? categories[0]?.id);
  const [selected, setSelected] = useState<string[]>(
    initial?.collections ?? (initialChoice ? [initialChoice] : []),
  );
  const [collection, setCollection] = useState(initialChoice ?? "");
  const [adding, setAdding] = useState(false);
  const [tags, setTags] = useState(initial?.tags.join(", ") ?? "");
  const [note, setNote] = useState(initial?.note ?? "");
  const [remember, setRemember] = useState(false);
  const [busy, setBusy] = useState(false);
  async function save(archive: boolean) {
    if (busy || adding) return;
    const current = selected.filter((id) => categories.some((c) => c.id === id));
    if (current.length !== selected.length) {
      setSelected(current);
      toast("A category was removed elsewhere. Review your choices and save again.");
      return;
    }
    const preferred = selected.includes(collection) ? collection : selected[0];
    const parsedTags = [
      ...new Set(
        tags
          .split(",")
          .map((t) => t.trim())
          .filter(Boolean),
      ),
    ];
    if (parsedTags.length > 12 || parsedTags.some((t) => t.length > 40)) {
      toast("Use up to 12 tags, with at most 40 characters each.");
      return;
    }
    setBusy(true);
    try {
      const result = await libraryApi.save({
        threadKey,
        collection: remember ? preferred : undefined,
        collections: selected,
        tags: parsedTags,
        note,
        remember: remember && selected.length > 0,
      });
      void client.invalidateQueries({ queryKey: libraryKey });
      registerUndo({
        message:
          remember && selected.length ? "Saved. Sender preference approved." : "Saved in MeeMail.",
        undo: async () => {
          await libraryApi.undo(result.undoId);
          void client.invalidateQueries({ queryKey: libraryKey });
        },
        doneMessage: () => "Save undone. The email is still in Gmail.",
      });
      if (archive && result.thread?.inInbox) {
        const action = await runThreadAction(
          client,
          result.thread,
          { kind: "gmail.archive_thread" },
          { search: {} },
        );
        if (!action) toast("Saved; this message is still in your inbox.");
      }
      onClose();
    } catch (error) {
      toast(categoryError(error, "Couldn’t save just now. Your notes are still here."));
    } finally {
      setBusy(false);
    }
  }
  return (
    <div className="flex w-[min(520px,90vw)] flex-col gap-3 p-5">
      <h2 className="text-2xl">Save for later</h2>
      <p className="text-sm break-words">
        <strong>{thread?.subject || initial?.title}</strong>
        <br />
        {thread?.latestFrom?.email || initial?.sender}
      </p>
      <p className="text-sm text-muted">
        Categories and tags live in MeeMail. Original emails and attachments stay in Gmail.
      </p>
      <fieldset className="flex flex-col gap-2" disabled={busy || adding}>
        <legend className="mb-2">Categories</legend>
        {categories.length ? (
          <div className="flex flex-wrap gap-2">
            {categories.map((c) => (
              <label
                key={c.id}
                className="flex items-center gap-2 rounded-lg border border-line px-3 py-2 text-sm"
              >
                <input
                  type="checkbox"
                  checked={selected.includes(c.id)}
                  onChange={(e) => {
                    setSelected((prev) =>
                      e.target.checked ? [...prev, c.id] : prev.filter((id) => id !== c.id),
                    );
                  }}
                />
                {c.name}
              </label>
            ))}
          </div>
        ) : (
          <p className="text-sm text-muted">
            No categories yet. Add one below, or save under All saved.
          </p>
        )}
        <p className="text-xs text-muted">
          Choose any categories, or none. Uncheck one to remove this email from it.
        </p>
      </fieldset>
      <CreateCategory
        disabled={busy}
        onBusy={setAdding}
        onCreated={(category) => setSelected((prev) => [...prev, category.id])}
      />
      <label className="flex flex-col gap-1">
        Tags (comma separated)
        <input
          className={field}
          value={tags}
          placeholder="2026, software, business"
          maxLength={490}
          onChange={(e) => setTags(e.target.value)}
        />
      </label>
      <label className="flex flex-col gap-1">
        Notes or excerpts
        <textarea
          className={field}
          rows={3}
          maxLength={4000}
          value={note}
          onChange={(e) => setNote(e.target.value)}
          placeholder="What you want to remember, or the parts to read later"
        />
      </label>
      <label className="flex items-start gap-2 text-sm">
        <input
          type="checkbox"
          disabled={!thread?.latestFrom?.email || !selected.length || busy || adding}
          checked={remember && !!selected.length}
          onChange={(e) => setRemember(e.target.checked)}
          className="mt-1"
        />
        <span>
          Learn from this choice: remember this sender’s category for future suggestions. You’ll
          approve every filing.
        </span>
      </label>
      {remember && selected.length > 1 ? (
        <label className="flex flex-col gap-1 text-sm">
          Category to suggest for this sender
          <select
            className={field}
            value={selected.includes(collection) ? collection : selected[0]}
            onChange={(e) => setCollection(e.target.value)}
          >
            {categories
              .filter((c) => selected.includes(c.id))
              .map((c) => (
                <option key={c.id} value={c.id}>
                  {c.name}
                </option>
              ))}
          </select>
        </label>
      ) : null}
      <div className="sticky -bottom-5 -mx-5 flex flex-wrap gap-2 border-t border-line bg-paper px-5 py-3">
        {thread ? (
          <Button variant="primary" busy={busy || adding} onClick={() => void save(true)}>
            Save & archive
          </Button>
        ) : null}
        <Button
          variant={thread ? undefined : "primary"}
          busy={busy || adding}
          onClick={() => void save(false)}
        >
          Save only
        </Button>
        <Button variant="ghost" disabled={busy || adding} onClick={onClose}>
          Cancel
        </Button>
      </div>
      <p className="text-xs text-muted">
        {thread
          ? "Save & archive removes this conversation from your inbox, with Undo. It remains in Saved and Gmail."
          : "This email is no longer cached here. You can edit its categories, tags and notes; open it in Gmail to archive it or teach sender suggestions."}
      </p>
    </div>
  );
}
export function SaveMailButton({
  threadKey,
  suggested,
  label = "Save",
  command,
}: {
  threadKey: string;
  suggested?: MailCollection;
  label?: string;
  /** The keymap command that opens this dialog (⌘K and its key), while `enabled`. */
  command?: { id: CommandId; enabled: boolean };
}) {
  const [open, setOpen] = useState(false);
  useCommand(command?.id ?? "card.save", () => setOpen(true), !!command?.enabled);
  const categories = useCategories();
  const item = useQuery({
    queryKey: [...libraryKey, "item", threadKey],
    queryFn: () => libraryApi.item(threadKey),
    enabled: open,
  });
  return (
    <>
      <Button size="sm" variant="secondary" onClick={() => setOpen(true)}>
        <Bookmark className="size-4" aria-hidden />
        {label}
      </Button>
      <Modal
        open={open}
        onClose={() => setOpen(false)}
        label="Save for later"
        className="mt-[5dvh] max-h-[90dvh]"
      >
        {item.isPending || categories.isPending ? (
          <p className="p-6">Loading saved details…</p>
        ) : item.isError || categories.isError ? (
          <div className="p-6">
            <p>Couldn’t load saved details.</p>
            <Button
              onClick={() => {
                void item.refetch();
                void categories.refetch();
              }}
            >
              Try again
            </Button>
          </div>
        ) : (
          <SaveForm
            key={threadKey}
            threadKey={threadKey}
            categories={categories.data ?? []}
            initial={item.data?.item ?? null}
            thread={item.data?.thread ?? null}
            suggested={suggested}
            onClose={() => setOpen(false)}
          />
        )}
      </Modal>
    </>
  );
}
